import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { makeStyles, FontWeights, ContextualMenu, ContextualMenuItemType, ContextualMenuItem, Icon } from '@fluentui/react';

import { LOCKED_ICON_NAME } from 'src/services/license';
import LockedFeatureCallout from 'src/components/base/LockedFeatureCallout';
import TeachingCoachmark from 'src/components/base/TeachingCoachmark';

import { useIdAsTarget } from 'shared/hooks';


const useCustomStyles = makeStyles(({ fonts }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    margin: '16px 16px 16px 0',
  },
  item: {
    position: 'relative',

    cursor: 'pointer',
    marginLeft: '16px',
    border: 'none',
    padding: 16,
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '96px',

    backgroundColor: '#f4f4f4',
    selectors: {
      ':hover': {
        backgroundColor: '#eaeaea',
      },
      ':active': {
        backgroundColor: '#c8c8c8',
      },
      '> i': {
        position: 'absolute',
        bottom: '5px',
        right: '5px',
      },
    },
  },
  itemDescription: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    height: '100%',
    textAlign: 'left',
  },
  itemTitle: {
    ...fonts.medium,
    fontWeight: FontWeights.semibold,
    color: '#333',
  },
  itemSecondary: {
    ...fonts.tiny,
    color: '#666',
  },

  placeholder: {
    position: 'relative',
    marginLeft: '16px',
    border: 'none',
    padding: 16,
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '96px',
  },
}))



const ListNav = ({ items, history }) => {
  const classNames = useCustomStyles();
  return (
    <div className={classNames.root}>
      { items.map((item, i) => item
        ? <NavItem key={i} {...item} history={history} />
        : <NavItemPlaceholder key={i} />)
      }
    </div>
  )
};

export function NavItemPlaceholder() {
  const classNames = useCustomStyles();
  return (<div className={classNames.placeholder} />);
}

export const NavItem = (props) => {
  const {
    to,
    title = '',
    secondaryText = '',
    onClick,
    licensing,
    teaching,
    history,
    menuItems = [],
    classes={ root: '' }
  } = props;
  const classNames = useCustomStyles();
  const [calloutOpenState, setCalloutOpen] = useState(false);
  const buttonRef = useRef(null);
  const [showContextualMenu, setShowContextualMenu] = useState(false);

  // buttonRef doesn't work well when changing route, but id does
  const [buttonId, teachingTarget] = useIdAsTarget('button');

  function handleOnClick(event) {
    if (menuItems.length) {
      setShowContextualMenu(true);
      return;
    }

    if (!onClick?.(event) && to) {
      history.push(to);
    }
  }

  const toggleCallout = (e) => setCalloutOpen(!calloutOpenState);
  const onHideContextualMenu = (e) => setShowContextualMenu(false);

  const { can = true, plans } = licensing || {};

   return (
    <>
    <button
      id={buttonId}
      ref={buttonRef}
      to={to}
      onClick={!can ? toggleCallout : handleOnClick}
      className={`${classNames.item} ${classes.root}`}
    >
      <span className={classNames.itemDescription}>
        <span className={classNames.itemTitle}>{title}</span>
        <span className={classNames.itemSecondary}>{secondaryText}</span>
      </span>
      {!can && <Icon iconName={LOCKED_ICON_NAME}/>}
    </button>
    <Menu
      items={menuItems}
      target={buttonRef}
      isHidden={!showContextualMenu}
      onDismiss={onHideContextualMenu}
    />
    { calloutOpenState &&
      <LockedFeatureCallout target={buttonRef} plans={plans} onDismiss={toggleCallout}/>
    }
    { teaching && <TeachingCoachmark target={teachingTarget} {...teaching} />}
    </>
  )
}


function Menu({ items, target, isHidden, onDismiss }) {
  // We cannot simply pass items to <ContextualMenu /> and override onRender
  // as it might be done with the most other components, because when we pass
  // items to <ContextualMenu/> it uses iconProps of each item
  // to calulate layout with margins. It also uses onClick of
  // each item thereby we cannot override them in MenuItem to
  // use with <ContextualMenuItem /> - it doesn't process onClick by its own
  // Or we might write our own MenuItem without reusing <ContextualMenuItem />

  const callbacks = useRef([]).current = useMemo(() => Array(items.length).fill({}), [items]);
  const setCallback = (index, cb) => callbacks[index] = { onClick: cb };

  const menuItems = useMemo(() => items.map((item, index) => {
    if (!item) {
      return { key: index, itemType: ContextualMenuItemType.Divider };
    }

    const { licensing = {} } = item;
    const { can = true } = licensing;

    const setOnClickCallback = (cb) =>  setCallback(index, cb);

    setOnClickCallback(item.onClick);
    const base = {
      key: index, ...item, setOnClickCallback,
      onClick: (event) => callbacks[index].onClick?.(event)
    };

    return can ? base : { ...base, iconProps: { iconName: LOCKED_ICON_NAME } };
  }), [items]);

  return (
    <>
      { !!menuItems.length &&
      <ContextualMenu
        contextualMenuItemAs={(props) => <MenuItem {...props} />}
        items={menuItems}
        hidden={isHidden}
        target={target}
        onDismiss={onDismiss}
      />
      }
    </>
  );
}


function MenuItem(props) {
  const { item, ...restProps } = props;
  const { setOnClickCallback } = item;

  const itemRef = useRef(null);
  const [isOpened, setIsOpened] = useState(false);
  const toggleCallout = (e) => {
    setIsOpened(!isOpened);
    e.preventDefault(); // Prevent closing menu
  }

  const { licensing = {}, ...restItem } = item;
  const { can = true, plans } = licensing;
  if (can) {
    return <ContextualMenuItem {...props} item={restItem} />;
  }

  setOnClickCallback(toggleCallout);

  return (
    <div ref={itemRef}>
      <ContextualMenuItem {...restProps} item={restItem}/>
      { isOpened &&
        // TODO: For some reason if we use onDismiss it dismisses callout even if move mouse
        // <LockedFeatureCallout target={menuItemRef} plans={plans} onDismiss={toggleCallout}/>
        <LockedFeatureCallout target={itemRef} plans={plans} />
      }
    </div>
  );
}


ListNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default withRouter(ListNav);
